import {
  assessmentProgressApi,
  companiesApi,
  type AssessmentProgressData,
  type FormulationProgressData,
  type InternalAssessmentProgressData,
} from '../services/api';
import { getFoundationProgress, type DashboardProgress } from './dashboard';

export interface CompanyProgressResult {
  progress: DashboardProgress | null;
  foundation: Record<string, unknown> | null;
  externalSections: AssessmentProgressData['sections'] | null;
  internalSections: InternalAssessmentProgressData['sections'] | null;
  formulationSections: FormulationProgressData['sections'] | null;
  unavailable: string[];
}

const isPercentage = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** Read the same saved content and live assessments used by the planning screens. */
export async function loadCompanyProgress(companyId: string): Promise<CompanyProgressResult> {
  const results = await Promise.allSettled([
    companiesApi.getStrategyProgress(companyId),
    companiesApi.loadFoundation(companyId),
    assessmentProgressApi.getExternalProgress(companyId),
    assessmentProgressApi.getInternalProgress(companyId),
    assessmentProgressApi.getFormulationProgress(companyId),
  ] as const);
  const unavailable: string[] = [];
  function read<T>(
    result: PromiseSettledResult<{ success: boolean; data?: T }>,
    source: string,
    valid: (value: T) => boolean
  ): T | null {
    if (
      result.status === 'fulfilled' &&
      result.value.success &&
      result.value.data !== undefined &&
      valid(result.value.data)
    )
      return result.value.data;
    unavailable.push(source);
    return null;
  }
  const stored = read(results[0], 'planning progress', (value) =>
    isPercentage(value?.executionProgress)
  );
  const saved = read(results[1], 'foundation', (value) => isRecord(value?.foundationData));
  const external = read(
    results[2],
    'external analysis',
    (value) =>
      isPercentage(value?.overall) &&
      ['pestel', 'fiveForces', 'competitors', 'drivingForces'].every((key) =>
        isPercentage(value?.sections?.[key as keyof AssessmentProgressData['sections']])
      )
  );
  const internal = read(
    results[3],
    'internal analysis',
    (value) =>
      isPercentage(value?.overall) &&
      isPercentage(value?.sections?.swot) &&
      isPercentage(value?.sections?.industrySuccessFactors)
  );
  const formulation = read(
    results[4],
    'strategy formulation',
    (value) =>
      isPercentage(value?.overall) &&
      isPercentage(value?.sections?.summary) &&
      isPercentage(value?.sections?.strategies)
  );
  const foundation = saved ? (saved.foundationData as Record<string, unknown>) : null;

  return {
    progress:
      stored && foundation && external && internal && formulation
        ? {
            foundationProgress: getFoundationProgress(foundation),
            externalAssessmentProgress: external.overall,
            internalAssessmentProgress: internal.overall,
            formulationProgress: formulation.overall,
            executionProgress: stored.executionProgress,
          }
        : null,
    foundation,
    externalSections: external ? { ...external.sections } : null,
    internalSections: internal ? { ...internal.sections } : null,
    formulationSections: formulation ? { ...formulation.sections } : null,
    unavailable,
  };
}
